import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import useActiveAccount from '@frontend/AppSettings/hooks/useActiveAccount';
import useActiveEmployeeId from '@frontend/AppSettings/hooks/useActiveEmployeeId';
import MatterService from '@frontend/AppSettings/services/MatterService';
import TaskService from '@frontend/AppSettings/services/TaskService';
import {
  isEngineeringEnvironment,
  isLegalEnvironment,
} from '@frontend/AppSettings/environmentConfig';
import { Matter } from '@frontend/Models/Matter/MatterModels';

import './WelcomeBanner.css';

export interface WelcomeBannerProps {
  className?: string;
}

/**
 * WelcomeBanner Component
 * 
 * Displays a welcome banner with:
 * - Personalized greeting with user's name
 * - Summary of active matters/projects and pending tasks
 * - Quick action buttons (New Matter, New Project)
 * - Decorative line graph icon
 * 
 * Features:
 * - Fetches user data from MSAL account
 * - Fetches matters/projects and tasks counts
 * - Navigation to create pages
 * - Responsive design
 */
const WelcomeBanner: React.FC<WelcomeBannerProps> = ({
  className = '',
}) => {
  const navigate = useNavigate();
  const account = useActiveAccount();
  const employeeId = useActiveEmployeeId();

  // Extract user's first name from account
  const userName = useMemo(() => {
    if (!account?.name) return 'User';
    const firstName = account.name.split(' ')[0];
    return firstName;
  }, [account]);

  // Fetch active matters count

  const { data: matters } = useQuery<Matter[]>({
    queryKey: ['Matters', 'dashboard', 'count', employeeId],
    queryFn: async () => {
      const response = employeeId
        ? await MatterService.getMattersByEmployee(employeeId)
        : await MatterService.getMatters();

      if (Array.isArray(response)) {
        return response;
      }

      return [response as Matter];

    },
  });

  // Fetch pending tasks count
  const { data: tasks } = useQuery({
    queryKey: ['Tasks', 'dashboard', 'count', employeeId],
    queryFn: () => 
      employeeId 
        ? TaskService.getTasksByEmployeeId(employeeId)
        : TaskService.getAllTasks(),
    enabled: !!employeeId || true,
  });

  // Calculate counts
  const activeMattersCount = useMemo(() => {
    if (!matters || !Array.isArray(matters)) return 0;
    return matters.filter((matter: any) => {
      const status = (matter.status || '').toLowerCase();
      return status === 'active' || status === 'in progress' || status === 'open';
    }).length;
  }, [matters]);

  const pendingTasksCount = useMemo(() => {
    if (!tasks || !Array.isArray(tasks)) return 0;
    return tasks.filter((task: any) => {
      const status = (task.status || '').toLowerCase();
      return status === 'pending' || status === 'in progress';
    }).length;
  }, [tasks]);

  // Navigation handlers
  const handleNewMatter = () => {
    navigate('/createMatters');
  };

  const handleNewProject = () => {
    navigate('/createProjects');
  };

  return (
    <div className={`welcome-banner ${className}`}>
      <div className="welcome-banner-content">
        {/* Left Section: Text and Buttons */}
        <div className="welcome-banner-left">
          <h1 className="welcome-banner-greeting">
            Welcome back, {userName}!
          </h1>
          <p className="welcome-banner-summary">
            You have {activeMattersCount} active {isEngineeringEnvironment ? 'projects' : 'matters'} and {pendingTasksCount} pending tasks today
          </p>
          <div className="welcome-banner-actions">
            {isLegalEnvironment && (
              <button
                type="button"
                className="welcome-banner-button welcome-banner-button-white"
                onClick={handleNewMatter}
              >
                <FontAwesomeIcon icon={faPlus} className="welcome-banner-button-icon" />
                New Matter
              </button>
            )}
            {isEngineeringEnvironment && (
              <button
                type="button"
                className="welcome-banner-button welcome-banner-button-navy"
                onClick={handleNewProject}
              >
                <FontAwesomeIcon icon={faPlus} className="welcome-banner-button-icon" />
                New Project
              </button>
            )}
          </div>
        </div>

        {/* Right Section: Decorative Graph Icon */}
        <div className="welcome-banner-right">
          <div className="welcome-banner-graph-icon">
            <svg
              viewBox="0 0 200 120"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="welcome-banner-graph-svg"
            >
              {/* X and Y axes */}
              <line
                x1="20"
                y1="100"
                x2="180"
                y2="100"
                stroke="currentColor"
                strokeWidth="2"
                opacity="0.3"
              />
              <line
                x1="20"
                y1="100"
                x2="20"
                y2="20"
                stroke="currentColor"
                strokeWidth="2"
                opacity="0.3"
              />
              {/* Wavy line representing data */}
              <path
                d="M 20 80 Q 60 60, 100 70 T 180 50"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Data points */}
              <circle cx="40" cy="75" r="3" fill="currentColor" />
              <circle cx="80" cy="65" r="3" fill="currentColor" />
              <circle cx="120" cy="70" r="3" fill="currentColor" />
              <circle cx="160" cy="55" r="3" fill="currentColor" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeBanner;

